/**
 * Film Comps API Service
 * Handles all Film Comps API communications with secure key management and error handling
 * Extends the existing API architecture for consistency
 */

import { apiConfig } from '../../config/apiConfig';

class CompsAPI {
  constructor() {
    // Use the existing API configuration as base
    this.baseURL = apiConfig.baseURL;
    this.apiKey = apiConfig.apiKey;
    this.timeout = apiConfig.timeout;
    this.retryAttempts = apiConfig.retryAttempts;
    this.debugMode = apiConfig.debugMode;
    
    if (this.debugMode) {
      console.log('🔧 CompsAPI initialized with config:', {
        baseURL: this.baseURL,
        timeout: this.timeout,
        retryAttempts: this.retryAttempts
      });
    }
  }

  /**
   * Get secure headers for API requests
   * @returns {Object} - Headers object
   */
  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
      'Accept': 'application/json'
    };
  }

  /**
   * Build full URL for endpoint
   * @param {string} endpoint - API endpoint
   * @returns {string} - Full URL
   */
  buildUrl(endpoint) {
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  /**
   * Sleep for specified milliseconds (for retry delays)
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} - Promise that resolves after delay
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Calculate exponential backoff delay
   * @param {number} attempt - Current attempt number (0-based)
   * @returns {number} - Delay in milliseconds
   */
  getRetryDelay(attempt) {
    return Math.min(1000 * Math.pow(2, attempt), 10000); // Max 10 seconds
  }

  /**
   * Classify error for handling strategy
   * @param {Error|Response} error - Error or response object
   * @returns {Object} - Error classification
   */
  classifyError(error) {
    if (error.name === 'AbortError') {
      return { type: 'timeout', retryable: true, userMessage: 'Request timed out. Please try again.' };
    }

    if (!navigator.onLine) {
      return { type: 'network', retryable: true, userMessage: 'No internet connection. Please check your network.' };
    }

    if (error.status) {
      const status = error.status;
      
      if (status >= 500) {
        return { type: 'server', retryable: true, userMessage: 'Server error. Please try again in a moment.' };
      }
      
      if (status === 429) {
        return { type: 'rateLimit', retryable: true, userMessage: 'Too many requests. Please wait a moment and try again.' };
      }
      
      if (status === 401 || status === 403) {
        return { type: 'auth', retryable: false, userMessage: 'Authentication failed. Please check your API configuration.' };
      }
      
      if (status === 404) {
        return { type: 'notFound', retryable: false, userMessage: 'API endpoint not found. Please check the configuration.' };
      }
      
      if (status >= 400) {
        return { type: 'client', retryable: false, userMessage: 'Invalid request. Please try again.' };
      }
    }

    return { type: 'unknown', retryable: true, userMessage: 'An unexpected error occurred. Please try again.' };
  }

  /**
   * Make HTTP request with retry logic
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<Object>} - Response data
   */
  async makeRequest(method, endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const { data, params } = options;

    // Add query parameters if provided
    const finalUrl = params ? `${url}?${new URLSearchParams(params)}` : url;

    const requestConfig = {
      method: method.toUpperCase(),
      headers: this.getHeaders(),
      signal: AbortSignal.timeout(this.timeout)
    };

    // Add body for POST/PUT requests
    if (data && ['POST', 'PUT', 'PATCH'].includes(requestConfig.method)) {
      requestConfig.body = JSON.stringify(data);
    }

    let lastError;
    
    for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        if (this.debugMode && attempt > 0) {
          console.log(`🔄 CompsAPI retry attempt ${attempt} for ${method.toUpperCase()} ${endpoint}`);
        }

        if (this.debugMode) {
          console.log(`📡 CompsAPI Request: ${method.toUpperCase()} ${finalUrl}`);
        }

        const response = await fetch(finalUrl, requestConfig);

        if (this.debugMode) {
          console.log(`📡 CompsAPI Response: ${response.status} ${response.statusText}`);
        }

        if (!response.ok) {
          const errorData = await response.text();
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          error.status = response.status;
          error.response = response;
          error.data = errorData;
          throw error;
        }

        const responseData = await response.json();
        
        if (this.debugMode) {
          console.log('✅ CompsAPI Response data:', responseData);
        }

        return responseData;

      } catch (error) {
        lastError = error;
        const errorInfo = this.classifyError(error);

        if (this.debugMode) {
          console.error(`❌ CompsAPI Error (attempt ${attempt + 1}):`, {
            type: errorInfo.type,
            retryable: errorInfo.retryable,
            message: error.message
          });
        }

        // Don't retry if error is not retryable or we've exhausted attempts
        if (!errorInfo.retryable || attempt === this.retryAttempts) {
          break;
        }

        // Wait before retry with exponential backoff
        if (attempt < this.retryAttempts) {
          const delay = this.getRetryDelay(attempt);
          if (this.debugMode) {
            console.log(`⏳ Waiting ${delay}ms before retry...`);
          }
          await this.sleep(delay);
        }
      }
    }

    // All retries failed, throw the last error
    const errorInfo = this.classifyError(lastError);
    const apiError = new Error(errorInfo.userMessage);
    apiError.originalError = lastError;
    apiError.type = errorInfo.type;
    throw apiError;
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Response data
   */
  async get(endpoint, params = {}) {
    return this.makeRequest('GET', endpoint, { params });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} - Response data
   */
  async post(endpoint, data = {}) {
    return this.makeRequest('POST', endpoint, { data });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @returns {Promise<Object>} - Response data
   */
  async put(endpoint, data = {}) {
    return this.makeRequest('PUT', endpoint, { data });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} - Response data
   */
  async delete(endpoint) {
    return this.makeRequest('DELETE', endpoint);
  }

  // ==================== FILM COMPS API ENDPOINTS ====================

  /**
   * Get film comps for a specific film
   * @param {string} filmId - IMDB ID of the film
   * @param {Object} options - Query options
   * @param {string} options.type - Filter by comp type ('modeled', 'greenlight', or 'all')
   * @param {number} options.limit - Maximum number of comps to return (default: 50)
   * @param {number} options.offset - Number of comps to skip (default: 0)
   * @returns {Promise<Object>} - Film comps data
   */
  async getFilmComps(filmId, options = {}) {
    try {
      console.log('🎬 Fetching film comps for:', filmId);
      
      const params = {};
      if (options.type) params.type = options.type;
      if (options.limit) params.limit = options.limit;
      if (options.offset) params.offset = options.offset;
      
      const endpoint = apiConfig.endpoints.filmComps.getFilmComps.replace('{filmId}', filmId);
      const response = await this.get(endpoint, params);
      
      if (!response.success) {
        throw new Error('Failed to fetch film comps');
      }

      console.log('✅ Film comps fetched successfully:', {
        filmId,
        totalCount: response.data?.totalCount || 0,
        modeledCount: response.data?.modeledCount || 0,
        greenlightCount: response.data?.greenlightCount || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch film comps:', error.message);
      throw error;
    }
  }

  /**
   * Get comparison data for a film
   * @param {string} filmId - IMDB ID of the film
   * @param {Object} options - Query options
   * @param {number} options.daysUntilRelease - Specific days until release for alignment
   * @param {number} options.weeksUntilRelease - Specific weeks until release for alignment
   * @returns {Promise<Object>} - Comparison data
   */
  async getComparisonData(filmId, options = {}) {
    try {
      console.log('📊 Fetching comparison data for:', filmId);
      
      const params = {};
      if (options.daysUntilRelease) params.daysUntilRelease = options.daysUntilRelease;
      if (options.weeksUntilRelease) params.weeksUntilRelease = options.weeksUntilRelease;
      
      const endpoint = apiConfig.endpoints.filmComps.getComparisonData.replace('{filmId}', filmId);
      const response = await this.get(endpoint, params);
      
      if (!response.success) {
        throw new Error('Failed to fetch comparison data');
      }

      console.log('✅ Comparison data fetched successfully:', {
        filmId,
        totalComps: response.data?.summary?.totalComps || 0,
        averageConfidenceScore: response.data?.summary?.averageConfidenceScore || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch comparison data:', error.message);
      throw error;
    }
  }

  /**
   * Get comp candidates for a film
   * @param {string} filmId - IMDB ID of the film
   * @param {number} limit - Maximum candidates to return (default: 20)
   * @returns {Promise<Object>} - Comp candidates data
   */
  async getCandidates(filmId, limit = 20) {
    try {
      console.log('🔍 Fetching comp candidates for:', filmId);
      
      const endpoint = apiConfig.endpoints.filmComps.getCandidates.replace('{filmId}', filmId);
      const response = await this.get(endpoint, { limit });
      
      if (!response.success) {
        throw new Error('Failed to fetch comp candidates');
      }

      console.log('✅ Comp candidates fetched successfully:', {
        filmId,
        candidatesCount: response.data?.candidates?.length || 0,
        totalCount: response.data?.totalCount || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch comp candidates:', error.message);
      throw error;
    }
  }

  /**
   * Assign a greenlight comp
   * @param {Object} assignment - Assignment data
   * @param {string} assignment.primaryFilmId - IMDB ID of the primary film
   * @param {string} assignment.compFilmId - IMDB ID of the comp film
   * @param {string} assignment.reason - Reason for assignment
   * @param {string} assignment.assignedBy - User ID of the assigner
   * @returns {Promise<Object>} - Assignment result
   */
  async assignComp(assignment) {
    try {
      console.log('➕ Assigning greenlight comp:', {
        primaryFilmId: assignment.primaryFilmId,
        compFilmId: assignment.compFilmId,
        assignedBy: assignment.assignedBy
      });
      
      const response = await this.post(apiConfig.endpoints.filmComps.assignComp, assignment);
      
      if (!response.success) {
        throw new Error('Failed to assign comp');
      }

      console.log('✅ Comp assigned successfully:', {
        compId: response.data?.id,
        primaryFilmId: response.data?.primaryFilmId,
        compFilmId: response.data?.compFilmId
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to assign comp:', error.message);
      throw error;
    }
  }

  /**
   * Update a greenlight comp
   * @param {string} compId - ID of the comp to update
   * @param {Object} updates - Updates to apply
   * @param {string} updates.reason - Updated reason
   * @param {boolean} updates.isActive - Active status
   * @returns {Promise<Object>} - Update result
   */
  async updateComp(compId, updates) {
    try {
      console.log('✏️ Updating comp:', compId);
      
      const endpoint = apiConfig.endpoints.filmComps.updateComp.replace('{compId}', compId);
      const response = await this.put(endpoint, updates);
      
      if (!response.success) {
        throw new Error('Failed to update comp');
      }

      console.log('✅ Comp updated successfully:', {
        compId: response.data?.id,
        updatedAt: response.data?.updatedAt
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to update comp:', error.message);
      throw error;
    }
  }

  /**
   * Delete a greenlight comp
   * @param {string} compId - ID of the comp to delete
   * @returns {Promise<Object>} - Delete result
   */
  async deleteComp(compId) {
    try {
      console.log('🗑️ Deleting comp:', compId);
      
      const endpoint = apiConfig.endpoints.filmComps.deleteComp.replace('{compId}', compId);
      console.log('🔗 Delete endpoint:', endpoint);
      
      const response = await this.delete(endpoint);
      console.log('📥 Delete response:', response);
      
      if (!response.success) {
        throw new Error('Failed to delete comp');
      }

      console.log('✅ Comp deleted successfully:', compId);
      
      return response;

    } catch (error) {
      console.error('❌ Failed to delete comp:', error.message);
      console.error('❌ Full error:', error);
      throw error;
    }
  }

  /**
   * Bulk assign comps
   * @param {Array} assignments - Array of assignment objects
   * @param {string} assignedBy - User ID of the assigner
   * @returns {Promise<Object>} - Bulk assignment result
   */
  async bulkAssign(assignments, assignedBy) {
    try {
      console.log('📦 Bulk assigning comps:', {
        assignmentsCount: assignments.length,
        assignedBy
      });
      
      const response = await this.post(apiConfig.endpoints.filmComps.bulkAssign, { assignments, assignedBy });
      
      if (!response.success) {
        throw new Error('Failed to bulk assign comps');
      }

      console.log('✅ Bulk assignment completed:', {
        totalProcessed: response.data?.totalProcessed || 0,
        successful: response.data?.successful || 0,
        failed: response.data?.failed || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to bulk assign comps:', error.message);
      throw error;
    }
  }

  /**
   * Get analytics summary
   * @returns {Promise<Object>} - Analytics summary data
   */
  async getAnalytics() {
    try {
      console.log('📈 Fetching comps analytics...');
      
      const response = await this.get(apiConfig.endpoints.filmComps.getAnalytics);
      
      if (!response.success) {
        throw new Error('Failed to fetch analytics');
      }

      console.log('✅ Analytics fetched successfully:', {
        totalComps: response.data?.overview?.totalComps || 0,
        modeledComps: response.data?.overview?.modeledComps || 0,
        greenlightComps: response.data?.overview?.greenlightComps || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch analytics:', error.message);
      throw error;
    }
  }

  /**
   * Get comp history
   * @param {string} compId - ID of the comp
   * @returns {Promise<Object>} - Comp history data
   */
  async getCompHistory(compId) {
    try {
      console.log('📜 Fetching comp history for:', compId);
      
      const endpoint = apiConfig.endpoints.filmComps.getCompHistory.replace('{compId}', compId);
      const response = await this.get(endpoint);
      
      if (!response.success) {
        throw new Error('Failed to fetch comp history');
      }

      console.log('✅ Comp history fetched successfully:', {
        compId,
        historyCount: response.data?.history?.length || 0
      });
      
      return response;

    } catch (error) {
      console.error('❌ Failed to fetch comp history:', error.message);
      throw error;
    }
  }
}

// Create and export singleton instance
const compsAPI = new CompsAPI();

export default compsAPI;
export { CompsAPI };
